import React, { useMemo, useEffect } from "react";

import { useTranslation } from "react-i18next";

import { useAppState } from "../../../../../context/StateContext";
import { CountryCode } from "../../../../../util/enums";
import CartonSSCC from "../../../../Generic/CartonSSCC";
import { CartonSSCCFrom } from "../../../../Generic/CartonSSCC/util";
import SideView from "../../../../Generic/SideView";
import DefaultViewSkeleton from "../../../../Generic/SideView/Skeleton";
import Documents from "../../../../Shipments/Maintenance/Documents";
import useActionBooking from "../../../actions";
import { useBookingContext } from "../../../context/Booking";
import { useBookingFiles } from "../../../hooks/useBookingFiles";
import { useDocuments } from "../../../hooks/useDocuments";
import { useBookingResourcesContext } from "../../../hooks/useResourcesContext";
import CbmWeight from "../../../Maintenance/CbmWeight";
import MarkAndNumbers from "../../../Maintenance/MarkAndNumbers";
import BookingOrders from "../../../Maintenance/Orders";
import { BookingSummary } from "../../../Maintenance/Summary";
import {
  calculateBookingCartonTotals,
  calculatePurchaseOrderTotal,
  calculateScannedItemsFromBooking,
  isBookingAvailableToGenerateDocument,
} from "../../../Maintenance/util";
import { BookingObjects, BookingTabs } from "../../../util";
import { BookingViewTab } from "../../types";
import { BookingViewCommercialInvoice } from "../CommercialInvoice";
import { BookingViewDetails } from "../Details";

interface BookingViewHandles {
  keyId: number | undefined;
  onCloseSidebar: () => void;
  isSidebarOpened: boolean;
}

export const BookingViewTabs = ({ keyId, isSidebarOpened, onCloseSidebar }: BookingViewHandles) => {
  const { getBookingById, getOrdersByBooking, getCartonsByBooking } = useActionBooking();
  const { t: translate } = useTranslation();
  const { booking, setBooking, handleUpdateBooking, handleChangeOptions, changedOptions } =
    useBookingContext();
  const { isFMVersion, isFMorSPVersion } = useAppState();
  const resourceOrders = useMemo(() => booking?.purchaseOrders || [], [booking?.purchaseOrders]);
  const {
    onDownloadDocument,
    onDownloadDocumentCsv,
    loadingCartonLogs,
    loadingBookingDocuments,
    loadingCiplCsv,
    loadingAllDocumentsZip,
    onDownloadAllDocuments,
    loadingSavingCsvDocument,
    saveShipmentDocuments,
    loadingUpdateShipmentDocuments,
  } = useBookingFiles({
    bookingId: booking?.id,
    hasBookingId: !!booking?.id,
    documents: booking?.documents,
  });

  const { loading: loadingResources, portList, countryList, companyDocuments } = useBookingResourcesContext();
  const { documentsJoinned } = useDocuments({ documents: booking?.documents });
  const { data: bookingData, isInitialLoading: loadingBooking } = getBookingById(Number(keyId));
  const { data: ordersBookingData, isInitialLoading: loadingOrders } = getOrdersByBooking(
    Number(keyId)
  );
  const { data: cartonsBookingData, isInitialLoading: loadingCartons } = getCartonsByBooking(
    Number(keyId)
  );

  const isDischargePortFromBrazil = useMemo(() => {
    let countryCode;

    if (isFMorSPVersion) countryCode = booking?.portOfDischarge?.country?.code;
    else
      countryCode =
        portList.find((port) => port.id === booking?.portOfDischargeId)?.country?.code ??
        "countryCode";

    return countryCode === CountryCode.BRAZIL;
  }, [booking?.portOfDischargeId, portList]);

  const ORIGIN_CRITERION_ORIGIN_COUNTRIES = [
    CountryCode.BANGLADESH,
    CountryCode.INDIA,
    CountryCode.CAMBODIA,
    CountryCode.INDONESIA,
    CountryCode.PAKISTAN,
  ];

  const showOriginCriterion = useMemo(() => {
    const ciCountry = countryList?.find((c) => c.id === booking?.commercialInvoice?.countryId);
    const hasQualifyingOrigin =
      !!ciCountry?.code &&
      ORIGIN_CRITERION_ORIGIN_COUNTRIES.includes(ciCountry.code as CountryCode);

    const destinationCode =
      booking?.portOfDestination?.country?.code ??
      booking?.portOfDischarge?.country?.code ??
      portList.find((port) => port.id === booking?.portOfDestinationId)?.country?.code ??
      portList.find((port) => port.id === booking?.portOfDischargeId)?.country?.code;

    return !!hasQualifyingOrigin && destinationCode === CountryCode.UNITED_KINGDOM;
  }, [booking?.commercialInvoice?.countryId, booking?.portOfDestinationId, booking?.portOfDestination, booking?.portOfDischargeId, booking?.portOfDischarge, portList, countryList]);

  const onUpdateBooking = (attribute: string, value: any) => {
    handleUpdateBooking(attribute, value);
  };

  useEffect(() => {
    if (bookingData) {
      setBooking((prev) => ({
        ...prev,
        ...bookingData,
      }));
    }
  }, [bookingData]);

  useEffect(() => {
    if (cartonsBookingData) {
      setBooking((prev) => ({
        ...prev,
        ...calculateBookingCartonTotals(cartonsBookingData),
      }));
    }
  }, [cartonsBookingData]);

  useEffect(() => {
    if (ordersBookingData && cartonsBookingData) {
      setBooking((prev) => ({
        ...prev,
        purchaseOrders: calculatePurchaseOrderTotal(ordersBookingData, cartonsBookingData),
      }));
    }
  }, [ordersBookingData, cartonsBookingData]);

  const purchaseOrders = useMemo(() => {
    let orders: string = "";

    if (bookingData?.purchaseOrders && bookingData?.purchaseOrders.length > 0) {
      const purchaseOrders = bookingData?.purchaseOrders;

      for (const purchaseOrder of purchaseOrders) {
        orders += purchaseOrder?.reference ? `${purchaseOrder?.reference} - ` : "";
      }
      orders = orders.slice(0, -2);

      return orders;
    }

    return "";
  }, [bookingData?.purchaseOrders]);

  const sidebarTitle = useMemo(() => {
    let title = translate("booking");
    const reference = bookingData?.containerId;

    if (reference) title = `${title} #${reference}`;

    return title;
  }, [bookingData]);

  const receivedCartons = useMemo(
    () => booking?.cartons?.filter((carton) => carton.isReceivedByCfs) || [],
    [booking?.cartons]
  );

  useEffect(() => {
    if (changedOptions?.[BookingObjects.DOCUMENTS]) {
      saveShipmentDocuments();
    }
  }, [booking?.documents, changedOptions]);

  const tabs = useMemo(() => {
    const tabs: Array<BookingViewTab> = [
      {
        key: BookingTabs.DETAILS,
        label: translate(BookingTabs.DETAILS),
        loading: loadingBooking,
        isEmpty: !bookingData,
        isOpened: false,
        render: () => <BookingViewDetails booking={bookingData} purchaseOrders={purchaseOrders} />,
        renderLoading: () => <DefaultViewSkeleton />,
      },
      {
        key: BookingTabs.MARK_AND_NUMBERS,
        label: translate(BookingTabs.MARK_AND_NUMBERS),
        loading: loadingBooking,
        isEmpty: !bookingData?.marks?.length,
        isOpened: false,
        render: () => (
          <MarkAndNumbers
            openedFromView
            marks={booking?.marks || []}
            viewOnly={{
              fields: true,
              actions: true,
            }}
            loading={loadingBooking}
          />
        ),
        renderLoading: () => <DefaultViewSkeleton />,
      },
      {
        key: BookingTabs.ORDERS,
        label: translate(BookingTabs.ORDERS),
        loading: loadingOrders,
        isEmpty: !ordersBookingData,
        isOpened: false,
        render: () => (
          <BookingOrders
            openedFromView
            purchaseOrders={ordersBookingData || []}
            viewOnly={{
              fields: true,
              actions: true,
            }}
            loadingOrders={loadingOrders}
          />
        ),
        renderLoading: () => <DefaultViewSkeleton />,
      },
      {
        key: BookingTabs.COMMERCIAL_INVOICE,
        label: translate(BookingTabs.COMMERCIAL_INVOICE),
        loading: loadingBooking,
        isEmpty: !bookingData,
        isOpened: false,
        render: () => (
          <BookingViewCommercialInvoice
            commercialInvoice={bookingData?.commercialInvoice}
            booking={bookingData}
          />
        ),
        renderLoading: () => <DefaultViewSkeleton />,
      },
      {
        key: BookingTabs.SSCC,
        label: translate(BookingTabs.SSCC),
        loading: loadingCartons || loadingResources,
        isEmpty: !booking?.cartons,
        isOpened: false,
        render: () => (
          <CartonSSCC
            openedFromView
            cartons={booking?.cartons || []}
            loadingCartons={false}
            viewOnly={{
              fields: true,
              actions: true,
            }}
            viewSupplier={true}
            calledFrom={CartonSSCCFrom.BOOKING}
            subtitle={{
              quantity: calculateScannedItemsFromBooking(booking),
              text: translate("scannedItems"),
            }}
            booking={booking}
            resources={{
              purchaseOrders: resourceOrders,
            }}
            isLengthMandatory={isDischargePortFromBrazil}
            showOriginCriterion={showOriginCriterion}
            statusBooking={booking?.statusShipment}
          />
        ),
        renderLoading: () => <DefaultViewSkeleton />,
      },
      ...(isFMVersion && !!booking
        ? [
            {
              key: BookingTabs.VOLUME_AND_WEIGHT,
              label: translate(BookingTabs.VOLUME_AND_WEIGHT),
              loading: loadingBooking,
              isEmpty: !receivedCartons,
              isOpened: false,
              render: () => (
                <CbmWeight
                  openedFromView
                  booking={booking}
                  cartons={receivedCartons}
                  loading={loadingBooking}
                  viewOnly={true}
                />
              ),
              renderLoading: () => <DefaultViewSkeleton />,
            },
          ]
        : []),
      {
        key: BookingTabs.DOCUMENTS,
        label: translate(BookingTabs.DOCUMENTS),
        loading: loadingBooking,
        isEmpty: !documentsJoinned,
        isOpened: false,
        render: () => (
          <Documents
            companyDocuments={companyDocuments}
            loadingResources={loadingResources}
            shipmentDocuments={documentsJoinned}
            openedFromView
            isBooking
            viewOnly={false}
            loadingShipment={loadingBooking}
            onDownloadDocument={
              isBookingAvailableToGenerateDocument(booking) ? onDownloadDocument : undefined
            }
            onDownloadDocumentCsv={onDownloadDocumentCsv}
            loadingAllDocuments={
              loadingBookingDocuments ||
              loadingCiplCsv ||
              loadingCartonLogs ||
              loadingAllDocumentsZip ||
              loadingUpdateShipmentDocuments
            }
            onDownloadAllDocuments={onDownloadAllDocuments}
            onUpdateShipment={(attribute, value) => {
              onUpdateBooking(attribute, value);
              handleChangeOptions(BookingObjects.DOCUMENTS);
            }}
            loadingUploadCsvDocument={loadingSavingCsvDocument}
          />
        ),
        renderLoading: () => <DefaultViewSkeleton />,
      },
      ...(isFMVersion
        ? [
            {
              key: BookingTabs.SUMMARY,
              label: translate(BookingTabs.SUMMARY),
              loading: loadingBooking,
              isEmpty: !documentsJoinned,
              isOpened: false,
              render: () => (
                <BookingSummary
                  booking={booking}
                  loadingData={loadingBooking || loadingCartons}
                  viewOnly={true}
                />
              ),
              renderLoading: () => <DefaultViewSkeleton />,
            },
          ]
        : []),
    ];

    return tabs;
  }, [
    bookingData,
    purchaseOrders,
    loadingBooking,
    ordersBookingData,
    loadingOrders,
    cartonsBookingData,
    loadingCartons,
    booking,
    documentsJoinned,
    loadingResources,
    companyDocuments,
    documentsJoinned,
    loadingBookingDocuments,
    loadingCartonLogs,
    loadingAllDocumentsZip,
    loadingUpdateShipmentDocuments,
    showOriginCriterion,
  ]);

  return (
    <SideView
      isLoading={loadingBooking}
      isOpened={isSidebarOpened}
      onClose={onCloseSidebar}
      tabs={tabs}
      title={sidebarTitle}
      buttonsComponent={[]}
      defaultTab={BookingTabs.DETAILS}
    />
  );
};
